import { FunctionComponent, PropsWithChildren, useState } from 'react';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useLocation, Navigate } from 'react-router-dom';
import { useIsAuthorized } from '@/hooks/useIsAuthorized';
import UnAuthorized from '@/pages/UnAuthorized';
import { PageWithLayout } from '@/models/page';
import { useSlice } from '@/hooks/useSlice';
import { authSlice } from '@starjack/features/authentication';

type AuthGuardProps = {
    Component: PageWithLayout;
};

export const AuthGuard: FunctionComponent<
    PropsWithChildren<AuthGuardProps>
> = ({ children, Component }) => {
    const {
        isLoggedIn,
        saveAuthDataFromUrl,
        authenticateFromLocalStorageData,
    } = useAuthentication();
    const {
        state: { logoutType },
    } = useSlice(authSlice, 'authSlice');
    const location = useLocation();
    const pathname = location.pathname;
    const { isProtected } = Component;
    const [isAuthorized] = useIsAuthorized(Component.permissions || []);
    const [loggedIn] = isLoggedIn();
    const [loginCheckDone, setLoginCheckDone] = useState(loggedIn);

    if (!isProtected) {
        return <>{children}</>;
    }

    if (!loginCheckDone) {
        const authDataSaved = saveAuthDataFromUrl();
        if (authDataSaved) {
            // Remove only auth-related URL params while preserving others
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('jwt');
            searchParams.delete('refreshCode');
            searchParams.delete('validFor');
            searchParams.delete('liftOperatorName');

            const newSearch = searchParams.toString();
            const newPath = newSearch ? `${pathname}?${newSearch}` : pathname;

            // Use window.history.replaceState for immediate URL update
            window.history.replaceState(null, '', newPath);

            setLoginCheckDone(true);
            return null;
        } else {
            authenticateFromLocalStorageData();
            setLoginCheckDone(true);
        }

        return null;
    }

    if (loggedIn) {
        if (!isAuthorized) {
            const getLayout = Component.getLayout ?? ((page) => page);

            return <>{getLayout(<UnAuthorized />)}</>;
        }

        return <>{children}</>;
    } else {
        const redirectUrl =
            logoutType === 'userLogout'
                ? ''
                : `?redirectUrl${encodeURIComponent(pathname)}`;
        return <Navigate to={`/login${redirectUrl}`} />;
    }
};
